import { CommonModule } from '@angular/common';
import {
  Component,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  model,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { HelpIconComponent } from '../../../help/components/help-icon/help-icon.component';
import { Asset } from '../../../home/models/asset.model';
import { SemerisTooltipDirective } from '../../../shared/directives/semeris-tooltip.directive';
import { ReadablePipe } from '../../../shared/pipes/readable-list.pipe';
import { ButtonDirective } from '../../../ui/button/button/button.directive';

@Component({
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatCheckboxModule,
    SemerisTooltipDirective,
    ReadablePipe,
    HelpIconComponent,
    ButtonDirective,
  ],
  selector: 'semeris-asset-selector',
  templateUrl: './asset-selector.component.html',
  styleUrls: ['./asset-selector.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetSelectorComponent {
  public options = model<Asset[] | undefined>(undefined);

  public readonly mode = input<'small' | string>(undefined);

  public readonly valueChanged = output<string[]>();

  public readonly label = computed<string[]>(() =>
    this.options()
      .filter((o) => o.selected && o.displayName)
      .map((o) => o.displayName)
  );

  public onValueChanged(assetShortName: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    const nextValue = this.options().map((o) =>
      o.assetShortName === assetShortName ? { ...o, selected: !o.selected } : o
    );

    this.options.set(nextValue);
  }

  public onAssetClicked(assetShortName: string) {
    this.options.set(
      this.options()?.map((o) => ({ ...o, selected: o.assetShortName === assetShortName }))
    );
    this.save();
  }

  public save() {
    this.valueChanged.emit(this.value());
  }

  public onClosed(event: void | 'click' | 'keydown' | 'tab') {
    if (event !== 'click') {
      this.options.set(this.options());
    }
  }

  public readonly hasSelectedAssets = computed<boolean>(
    () => this.options()?.some((o) => o.selected) ?? false
  );

  private readonly value = computed<string[]>(() =>
    this.options()
      ?.filter((o) => o.selected)
      ?.map((o) => o.assetShortName)
  );
}
